// registration-wizard/ParticipantsStep.tsx
import { Stack, Card, Group, Text, ActionIcon, Tooltip, Select, Switch, Button } from '@mantine/core';
import { IconPlus, IconTrash, IconX, IconUser } from '@tabler/icons-react';
import { Member } from '@actschurch/ccms-sdk';
import { TextInput } from '@/components';
import { ParticipantDraft } from '../types';
import { MemberSearch } from './MemberSearch';

export interface ParticipantsStepProps {
  participants: ParticipantDraft[];
  searchTerms: string[];
  onUpdateSearchTerm: (index: number, val: string) => void;
  onMemberSelect: (index: number, member: Member) => void;
  onUpdateParticipant: (index: number, patch: Partial<ParticipantDraft>) => void;
  onClearParticipant: (index: number) => void;
  onAddParticipant: () => void;
  onRemoveParticipant: (index: number) => void;
}

export function ParticipantsStep({
  participants,
  searchTerms,
  onUpdateSearchTerm,
  onMemberSelect,
  onUpdateParticipant,
  onClearParticipant,
  onAddParticipant,
  onRemoveParticipant,
}: ParticipantsStepProps) {
  return (
    <Stack gap="sm">
      {participants.map((p, i) => (
        <Card key={i} withBorder radius="md" padding="sm">
          <Group justify="space-between" mb="xs">
            <Group gap="xs">
              <Text size="sm" fw={600}>Participant {i + 1}</Text>
              {p.resolved && <Text size="xs" c="dimmed">· Member #{p.memberID}</Text>}
            </Group>
            <Group gap={6}>
              {(p.resolved || p.name || p.surname || p.email || p.phone) && (
                <Tooltip label="Clear participant">
                  <ActionIcon variant="light" color="orange" size="sm" onClick={() => onClearParticipant(i)}>
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {participants.length > 1 && (
                <Tooltip label="Remove row">
                  <ActionIcon variant="light" color="red" size="sm" onClick={() => onRemoveParticipant(i)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          <MemberSearch
            searchTerm={searchTerms[i] ?? ''}
            onSearchChange={(val) => onUpdateSearchTerm(i, val)}
            onSelect={(member) => onMemberSelect(i, member)}
          />

          <Stack gap="xs" mt="xs">
            <Group grow>
              <TextInput
                label="Name"
                placeholder="First name"
                required
                value={p.name}
                leftSection={p.resolved ? <IconUser size={14} /> : undefined}
                onChange={(e) => { const v = e.currentTarget.value; onUpdateParticipant(i, { name: v, resolved: false }); }}
              />
              <TextInput
                label="Surname"
                placeholder="Surname"
                required
                value={p.surname}
                onChange={(e) => { const v = e.currentTarget.value; onUpdateParticipant(i, { surname: v, resolved: false }); }}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Email"
                placeholder="Email"
                value={p.email}
                onChange={(e) => { const v = e.currentTarget.value; onUpdateParticipant(i, { email: v }); }}
              />
              <TextInput
                label="Phone"
                placeholder="Phone"
                value={p.phone}
                onChange={(e) => { const v = e.currentTarget.value; onUpdateParticipant(i, { phone: v }); }}
              />
            </Group>

            <Select
              label="Gender"
              placeholder="Gender"
              data={['Male', 'Female']}
              value={p.gender}
              onChange={(val) => onUpdateParticipant(i, { gender: val })}
            />

            <Group gap="lg" mt={4}>
              <Switch
                label="Main participant"
                checked={p.isMain}
                onChange={(e) => onUpdateParticipant(i, { isMain: e.currentTarget.checked })}
              />
              <Switch
                label="Consent given"
                checked={p.hasConsent}
                onChange={(e) => onUpdateParticipant(i, { hasConsent: e.currentTarget.checked })}
              />
            </Group>
          </Stack>
        </Card>
      ))}

      <Button variant="light" leftSection={<IconPlus size={14} />} onClick={onAddParticipant}>
        Add Participant
      </Button>
    </Stack>
  );
}

export default ParticipantsStep;